import React, { useMemo, useState, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { PlusSmIcon, XIcon } from "@heroicons/react/outline";

import ReactTable from "components/lib/Global/ReactTable";
import { getFullName } from "utils";
import SubRowAsync from "./SubRowAsync";
import {
  INDIVIDUALS,
  OWNERS_ROLES,
} from "components/PLCTABS/SFLdata/config/constants";
import Checkbox from "components/lib/Shared/Checkbox";
import AddEmployee from "components/Employee/AddNewEmployee";
import { getOrganizationEmployeeAsync } from "state/slices/employee";

const AssignPermission = () => {
  const dispatch = useDispatch();
  const partners = useSelector((state) => state.partnersSlice);
  const [filters, setFilters] = useState([]);
  const { organization } = useSelector((state) => state.organizationsSlice);
  const currentUser = useSelector((state) => state.userSlice);

  const filteredPartners = partners.filter(
    (partner) => !partner.isCurrentUserRow && filters.includes(partner.role[0])
  );

  const columns = useMemo(
    () => [
      {
        Header: () => "Full Name",
        id: "fullName",
        accessor: "fullName",
        Cell: ({ row }) => getFullName(row.original.names) || "N/A",
      },
      {
        Header: () => "ID Type",
        id: "ID Type",
        accessor: "idType",
      },
      {
        Header: "Role",
        Cell: ({ row: { original } }) => original?.role?.join(", ") ?? "",
      },
      {
        id: "expander", // It needs an ID
        Cell: ({ row, rows }) => {
          const onClick = () => {
            const alreadyExpandedRow = rows.find((row) => row.isExpanded);
            if (
              alreadyExpandedRow?.getToggleRowExpandedProps &&
              alreadyExpandedRow?.isExpanded
            ) {
              alreadyExpandedRow?.getToggleRowExpandedProps().onClick();
            }
            if (alreadyExpandedRow?.id !== row.id) {
              row.getToggleRowExpandedProps().onClick();
            }
          };

          return (
            <span
              className="text-sm font-normal text-primary"
              {...row.getToggleRowExpandedProps()}
              onClick={onClick}
            >
              {row.isExpanded ? (
                <XIcon className="inline w-4 h-4 mr-1 text-tertiary" />
              ) : (
                <PlusSmIcon className="inline w-4 h-4 mr-1 text-tertiary" />
              )}
            </span>
          );
        },
        // We can override the cell renderer with a SubCell to be used with an expanded row
        SubCell: () => null, // No expander on an expanded row
      },
    ],
    []
  );
  const employeeColumns = useMemo(
    () => [
      {
        Header: "Full Name",
        accessor: "fullName",
        Cell: ({ row }) => getFullName(row?.original?.user?.names) || "N/A",
      },
      {
        Header: "ID Type",
        accessor: "idType",
        Cell: ({ row }) => row?.original?.user?.idType || "N/A",
      },
      {
        Header: "Role",
        Cell: "Employee",
      },
      {
        id: "expander",
        Cell: ({ row, rows }) => {
          const onClick = () => {
            const alreadyExpandedRow = rows.find((r) => r.isExpanded);
            alreadyExpandedRow?.getToggleRowExpandedProps().onClick();
            if (alreadyExpandedRow?.id !== row.id) {
              row.getToggleRowExpandedProps().onClick();
            }
          };

          if (row.original.user?._id === currentUser?._id) {
            return <span></span>;
          } else {
            return (
              <span
                className="text-sm font-normal text-primary"
                {...row.getToggleRowExpandedProps()}
                onClick={onClick}
              >
                {row.isExpanded ? (
                  <XIcon className="inline w-4 h-4 mr-1 text-tertiary" />
                ) : (
                  <PlusSmIcon className="inline w-4 h-4 mr-1 text-tertiary" />
                )}
              </span>
            );
          }
        },
      },
    ],
    []
  );
  // Create a function that will render our row sub components
  const renderRowSubComponent = React.useCallback(
    ({ row, rowProps, visibleColumns }) => (
      <SubRowAsync
        row={row}
        rowProps={rowProps}
        visibleColumns={visibleColumns}
      />
    ),
    []
  );

  useEffect(() => {
    if (organization?.partnershipType) {
      setFilters(
        OWNERS_ROLES[organization.partnershipType].map((role) => role.value)
      );
    }
  }, [organization.partnershipType]);
  const roles = OWNERS_ROLES[organization.partnershipType];
  const handleFilterChange = (e) => {
    if (e.target.checked) {
      setFilters([...filters, e.target.value]);
    } else {
      setFilters(filters.filter((filter) => filter !== e.target.value));
    }
  };
  const [employee, setEmployee] = useState(false);
  const [recallEmployeeTable, setRecallEmployeeTable] = useState(false);
  const renderEmployeeRowSubComponent = useCallback(
    ({ row, visibleColumns }) => (
      <SubRowAsync
        row={row}
        visibleColumns={visibleColumns}
        forEmployee={true}
        setRecallEmployeeTable={setRecallEmployeeTable}
      />
    ),
    []
  );
  useEffect(() => {
    if (organization?._id) {
      dispatch(
        getOrganizationEmployeeAsync({ organizationId: organization._id })
      );
    }
    setRecallEmployeeTable(false);
  }, [dispatch, organization, recallEmployeeTable]);
  const { getEmployee } = useSelector((state) => state.employeeSlice);
  return (
    <>
      {partners?.length > 0 || organization ? (
        <>
          {!employee && (
            <div>
              <div className="flex justify-between items-center mb-4">
                <p className="text-gray-500 text-sm">Officials</p>
                <button
                  className="bg-[#092c4c] text-white px-4 py-2 rounded shadow hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  onClick={() => setEmployee(true)}
                >
                  + Add Employee
                </button>
              </div>
              <hr />
            </div>
          )}
          {employee ? (
            <AddEmployee
              setEmployee={setEmployee}
              setRecallEmployeeTable={setRecallEmployeeTable}
              formType="organization"
            />
          ) : (
            <>
              {filteredPartners?.length > 0 && roles?.length > 0 && (
                <div>
                  {/** Partners filter and table */}
                  <p className="text-gray-500 text-sm mb-1">Filters</p>
                  <div className="flex space-x-2 my-2">
                    {roles.map((role) => (
                      <Checkbox
                        key={role.value}
                        name={role.label}
                        label={role.label}
                        checked={filters.includes(role.value)}
                        onChange={handleFilterChange}
                      />
                    ))}
                  </div>
                  <hr />
                  <div className="mt-6 w-full bg-white shadow">
                    <ReactTable
                      columns={columns}
                      data={filteredPartners}
                      renderRowSubComponent={renderRowSubComponent}
                      dataRowCellStyle="pl-4 leading-snug py-2"
                    />
                  </div>
                </div>
              )}

              <div>
                {/** Employees table */}
                {getEmployee.items.length > 0 && (
                  <div className="mt-6 w-full bg-white shadow">
                    <ReactTable
                      columns={employeeColumns}
                      data={getEmployee.items}
                      renderRowSubComponent={renderEmployeeRowSubComponent}
                      dataRowCellStyle="pl-4 leading-snug py-2"
                    />
                  </div>
                )}
              </div>
            </>
          )}
        </>
      ) : (
        <div className="text-center">
          <svg
            className="w-12 h-12 mx-auto text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              vectorEffect="non-scaling-stroke"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 13h6m-3-3v6m-9 1V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z"
            />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            No partners found
          </h3>
        </div>
      )}
    </>
  );
};

export default AssignPermission;
